"use client";

import { useState, useEffect, useRef } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

interface CountdownModalProps {
  isOpen: boolean;
  onComplete: () => void;
}

const SECONDS = 3;
const EASE_OUT = [0.23, 1, 0.32, 1] as const;

export default function CountdownModal({ isOpen, onComplete }: CountdownModalProps) {
  const [countdown, setCountdown] = useState<number | null>(null);
  const completedRef = useRef(false);
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    if (!isOpen) {
      setCountdown(null);
      completedRef.current = false;
      return;
    }

    // Start countdown when modal opens. The updater stays pure — completion is
    // handled by the effect below, not called from inside setCountdown.
    setCountdown(SECONDS);
    const interval = setInterval(() => {
      setCountdown((prev) => (prev === null || prev <= 0 ? prev : prev - 1));
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, [isOpen]);

  // Fire onComplete exactly once when the countdown reaches zero.
  useEffect(() => {
    if (isOpen && countdown === 0 && !completedRef.current) {
      completedRef.current = true;
      onComplete();
    }
  }, [isOpen, countdown, onComplete]);

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent
        showCloseButton={false}
        className="w-auto max-w-none border-0 bg-transparent p-0 shadow-none focus:outline-none"
      >
        <DialogTitle className="sr-only">Paper starting</DialogTitle>
        <div className="flex flex-col items-center gap-8">
          <div className="relative flex h-32 items-center justify-center overflow-hidden px-8">
            <AnimatePresence mode="popLayout" initial={false}>
              {countdown !== null && countdown > 0 && (
                <motion.span
                  key={countdown}
                  initial={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 28, filter: "blur(6px)" }}
                  animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                  exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: -28, filter: "blur(6px)" }}
                  transition={{ duration: 0.28, ease: EASE_OUT }}
                  className="text-[7rem] font-semibold leading-none tracking-tighter tabular-nums text-foreground"
                >
                  {countdown}
                </motion.span>
              )}
            </AnimatePresence>
          </div>
          <div className="flex items-center gap-2" aria-hidden>
            {Array.from({ length: SECONDS }, (_, i) => (
              <span
                key={i}
                className={cn(
                  "size-1.5 rounded-full transition-colors duration-200",
                  countdown !== null && SECONDS - countdown > i ? "bg-primary" : "bg-foreground/20",
                )}
              />
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
